import type { Page, PageMeta } from '../types/common.js';

/**
 * Unwrap the global success envelope applied by the backend's
 * `TransformInterceptor`: `{ statusCode, message, data }`.
 *
 * Falls through unchanged for anything that isn't shaped like the envelope, so
 * a future un-enveloped endpoint keeps working.
 */
export function unwrapEnvelope<T>(body: unknown): T {
  if (
    body !== null &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    'statusCode' in body &&
    'data' in body
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function syntheticMeta(count: number): PageMeta {
  return { page: 1, limit: count, totalItems: count, totalPages: count > 0 ? 1 : 0 };
}

/**
 * Normalize the three list shapes the API returns into a single `Page<T>`.
 *
 * - `{ data: T[], meta }` — the standard paginated response
 * - `T[]`                 — `GET /orders` (capped at 100) and `GET /banks`
 * - `{ items: T[] }`      — legacy shape the PHP client also tolerates
 */
export function unwrapList<T>(data: unknown): Page<T> {
  if (Array.isArray(data)) {
    return { data: data as T[], meta: syntheticMeta(data.length) };
  }

  if (data !== null && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const items = Array.isArray(obj.data)
      ? obj.data
      : Array.isArray(obj.items)
        ? obj.items
        : null;

    if (items) {
      const meta = obj.meta as Partial<PageMeta> | undefined;
      const fallback = syntheticMeta(items.length);
      return {
        data: items as T[],
        meta: {
          page: meta?.page ?? fallback.page,
          limit: meta?.limit ?? fallback.limit,
          totalItems: meta?.totalItems ?? fallback.totalItems,
          totalPages: meta?.totalPages ?? fallback.totalPages,
        },
      };
    }
  }

  return { data: [], meta: { page: 1, limit: 0, totalItems: 0, totalPages: 0 } };
}

/**
 * Convert a money field to a number of VND.
 *
 * Amounts are read back as strings (Prisma `Decimal`) but written as integers.
 * The SDK never coerces silently — call this when you need arithmetic.
 */
export function toVnd(amount: string | number | null | undefined): number {
  if (amount === null || amount === undefined) return 0;
  const n = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(n) ? n : 0;
}

/** Format a money field as Vietnamese currency, e.g. `100.000 ₫`. */
export function formatVnd(amount: string | number | null | undefined): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(toVnd(amount));
}
